import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../utils/organizationUtils';

export interface SeasonData {
  id: string;
  name: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  active: boolean;
}

export interface SeasonOption {
  id: string;
  name: string;
  start_date?: string;
  end_date?: string;
}

class SeasonsService {
  /**
   * Get all accessible seasons for the current user
   */
  async getSeasons(): Promise<SeasonOption[]> {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('id, name, start_date, end_date')
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching seasons:', error);
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSeasons:', error);
      throw error;
    }
  }

  /**
   * Get accessible seasons for the current organization
   */
  async getAccessibleSeasons(): Promise<SeasonOption[]> {
    try {
      // Get current organization ID
      const orgId = getCurrentOrgId();
      
      if (!orgId) {
        console.warn('No organization ID found for fetching accessible seasons');
        return [];
      }

      console.log('Fetching accessible seasons for organization:', orgId);
      
      const { data, error } = await supabase
        .from('season_access')
        .select(`
          season_id,
          seasons!inner(
            id,
            name,
            start_date,
            end_date
          )
        `)
        .eq('organisation_id', orgId) // Filter by current organization
        .eq('active', true)
        .eq('seasons.active', true)
        .order('seasons(name)');

      console.log('Raw data from season_access query:', data);
      console.log('Query error:', error);

      if (error) {
        console.error('Error fetching accessible seasons:', error);
        throw new Error(error.message);
      }

      // Transform the data to match SeasonOption interface
      const seasons = data?.map((item: any) => ({
        id: item.seasons.id,
        name: item.seasons.name,
        start_date: item.seasons.start_date,
        end_date: item.seasons.end_date
      })) || [];

      console.log('Transformed accessible seasons:', seasons);
      return seasons;
    } catch (error) {
      console.error('Error in getAccessibleSeasons:', error);
      throw error;
    }
  }

  /**
   * Get seasons for a specific tournament
   * Returns all accessible seasons (same approach as tournaments and teams)
   */
  async getSeasonsByTournament(tournamentId: string): Promise<SeasonOption[]> {
    try {
      console.log(`[SeasonsService] getSeasonsByTournament called for tournamentId: ${tournamentId}`);
      
      // Return all accessible seasons (same approach as tournaments and teams)
      const accessibleSeasons = await this.getAccessibleSeasons();
      
      console.log(`[SeasonsService] Returning all accessible seasons for tournamentId ${tournamentId}:`, accessibleSeasons);
      return accessibleSeasons;
    } catch (error) {
      console.error('Error in getSeasonsByTournament:', error);
      throw error;
    }
  }
}

export const seasonsService = new SeasonsService();
